import {
    type PaidSubscriptionTier,
    SUBSCRIPTION_INTERVALS,
    SUBSCRIPTION_TIERS,
    type SubscriptionInterval,
} from '@/lib/api/subscriptions';
import { useSubscriptionPrices } from '@/hooks/useSubscriptionPrices';
import { formatCurrency } from '@/lib/currency';
import { COMMON_FEATURES, PRO_FEATURES } from '../../constants';
import { PlanFeaturePill } from './PlanFeaturePill';
import { cn } from '@/lib/utils/cn';

interface PlanDetailCardProps {
    tier: PaidSubscriptionTier;
    interval: SubscriptionInterval;
    selected: boolean;
    isCurrentPlan: boolean;
    onCardClick: () => void;
}

export const PlanDetailCard = ({ tier, interval, selected, isCurrentPlan, onCardClick }: PlanDetailCardProps) => {
    const { currency, getPrice, getMonthlyAnnualizedPrice } = useSubscriptionPrices();

    const isCreatorPro = tier === SUBSCRIPTION_TIERS.CREATOR_PRO;
    const isYearly = interval === SUBSCRIPTION_INTERVALS.YEAR;

    const monthlyPrice = getPrice(tier, SUBSCRIPTION_INTERVALS.MONTH);
    const yearlyPrice = getPrice(tier, SUBSCRIPTION_INTERVALS.YEAR);
    const monthlyAnnualizedPrice = getMonthlyAnnualizedPrice(tier);

    // Calculate savings for yearly plan
    const yearlySavings = isYearly ? monthlyAnnualizedPrice - yearlyPrice : 0;

    return (
        <button
            onClick={onCardClick}
            className={`relative flex w-full cursor-pointer flex-col gap-5 rounded-2xl border-2 p-6 text-left transition-[background,border] duration-200 ease-in ${
                selected
                    ? 'border-brand-main shadow-nucleus-elevation-medium bg-canvas'
                    : 'border-transparent bg-transparent'
            }`}
        >
            <div
                className={cn(
                    'bg-brand-main text-inverse absolute -top-3 left-1/2 origin-center -translate-x-1/2 scale-y-0 rounded-full px-4 py-1.5 text-xs font-bold opacity-0 transition-[scale,opacity] duration-200 ease-in',
                    {
                        'scale-y-100 opacity-100': selected,
                    }
                )}
            >
                Selected
            </div>

            {/* Header Section */}
            <div className='flex flex-col gap-1'>
                <div className='flex items-center justify-between'>
                    {/* Plan Name */}
                    <div className='flex items-baseline gap-1'>
                        <span className='text-semantic-fg-base text-xl font-extrabold'>Creator</span>
                        {isCreatorPro && <span className='text-brand-main playfair text-xl font-extrabold'>Pro</span>}
                    </div>

                    {/* Current Plan or Savings Pill */}
                    {(isCurrentPlan || (isYearly && !isCurrentPlan)) && (
                        <div
                            className={`bg-brand-main/25 text-brand-main rounded-md px-2 py-1 text-xs font-bold ${
                                selected && 'bg-brand-subtle'
                            }`}
                        >
                            {isCurrentPlan ? 'Current Plan' : `Save ${formatCurrency(yearlySavings, currency)}/yr`}
                        </div>
                    )}
                </div>

                {/* Price Display */}
                <div className='flex items-baseline gap-2'>
                    {isYearly && (
                        <span className='text-semantic-fg-disabled text-sm font-bold line-through'>
                            {formatCurrency(monthlyAnnualizedPrice, currency)}/yr
                        </span>
                    )}
                    <span className='text-brand-main text-lg'>
                        <span className='font-bold'>
                            {formatCurrency(isYearly ? yearlyPrice : monthlyPrice, currency)}
                        </span>
                        /{isYearly ? 'yr' : 'mo'}
                    </span>
                </div>
            </div>

            {/* Features Section */}
            <div className='flex flex-col gap-4'>
                {/* Creator Pro: Show "Everything in Creator" text on mobile */}
                {isCreatorPro && (
                    <span className='text-semantic-fg-base block text-sm font-bold md:hidden'>
                        <span className='italic'>Everything</span> in Creator, plus...
                    </span>
                )}

                {/* Common Features (hidden on mobile for Creator Pro) */}
                <div className={`flex flex-col gap-2.5 ${isCreatorPro ? 'max-md:hidden' : ''}`}>
                    {COMMON_FEATURES.map(feature => (
                        <PlanFeaturePill key={`${feature.header}-common`} {...feature} />
                    ))}
                </div>

                {/* Pro Features */}
                {isCreatorPro && (
                    <div className='bg-semantic-accent-dim/25 border-semantic-fg-muted/70 flex flex-col gap-2.5 rounded-lg border border-dashed p-2.5'>
                        {PRO_FEATURES.map(feature => (
                            <PlanFeaturePill key={`${feature.header}-pro`} {...feature} />
                        ))}
                    </div>
                )}
            </div>
        </button>
    );
};
